import { z } from 'zod';

/**
 * Case Wiki — taxonomie des actes procéduraux pénaux.
 *
 * `procedural_act_type` reste l'acte concret. La phase procédurale stockée en
 * base vit désormais séparément dans `case_facts.procedural_phase` quand la
 * pièce l'établit explicitement. Le catalogue ci-dessous garde un fallback de
 * compatibilité pour les dossiers legacy, mais ce fallback ne doit jamais être
 * persisté comme s'il s'agissait d'une vérité source.
 */

export const ProceduralActTypeSchema = z.enum([
  // — enquête —
  'plainte',
  'pv_constatation',
  'pv_audition_victime',
  'pv_audition_temoin',
  'audition_libre',
  'garde_a_vue',
  'perquisition',
  'saisie_scelle',
  'requisition',
  'exploitation_video_telephonie',
  'prolongation_flagrance',
  'passage_preliminaire',
  // — instruction —
  'requisitoire_introductif',
  'commission_rogatoire',
  'interrogatoire_premiere_comparution',
  'mise_en_examen',
  'temoin_assiste',
  'demande_acte',
  'ordonnance',
  'expertise',
  'requisitoire_definitif',
  'ordonnance_reglement',
  // — jugement —
  'citation_convocation',
  'audience',
  'jugement_correctionnel',
  'arret',
  'decision_cour_assises',
  'appel',
  'pourvoi',
  // — legacy M110 gardées parseables pour les dossiers déjà ingérés —
  'acte_enquete',
  'ordonnance_soit_communique',
  'jugement_fond',
  'requete_nullite',
  'demande_mise_en_liberte',
  'observations_175',
  'appel_chambre_instruction',
  // — parquet / JLD / liberté ajoutés pour la phase procédurale explicite —
  'classement_sans_suite',
  'alternative_poursuites',
  'saisine_juridiction_jugement',
  'saisine_jld',
  'debat_jld',
  'ordonnance_jld',
  'placement_detention_provisoire',
  'placement_controle_judiciaire',
  'placement_arse',
  'ordonnance_mise_en_liberte',
]);
export type ProceduralActType = z.infer<typeof ProceduralActTypeSchema>;

/** Les 3 phases lisibles par l'avocat dans la Procédure pénale V1. */
export const ProceduralActPhaseSchema = z.enum(['enquete', 'instruction', 'jugement']);
export type ProceduralActPhase = z.infer<typeof ProceduralActPhaseSchema>;

/**
 * Raffinement de l'enquête stockée explicitement quand la source va au-delà de
 * la simple phase haute-niveau.
 */
export const ProceduralEnqueteKindSchema = z.enum([
  'flagrance',
  'preliminaire',
  'sui_generis',
  'unknown',
]);
export type ProceduralEnqueteKind = z.infer<typeof ProceduralEnqueteKindSchema>;
export const PROCEDURAL_ENQUETE_KIND_VALUES = ProceduralEnqueteKindSchema.options;

export const ENQUETE_KIND_INFO: Record<
  ProceduralEnqueteKind,
  {
    label: string;
  }
> = {
  flagrance: { label: 'Flagrance' },
  preliminaire: { label: 'Préliminaire' },
  sui_generis: { label: 'Sui generis' },
  unknown: { label: 'Non qualifiée' },
};

/**
 * Voie logique de l'acte, indépendante de la position visuelle.
 *   - `charge`   : actes d'enquête / instruction menés à charge
 *   - `ossature` : colonne vertébrale parquet → juge qui fait avancer la procédure
 *   - `defense`  : recours et demandes formés pour notre client
 */
export const ProceduralLaneSchema = z.enum(['charge', 'ossature', 'defense']);
export type ProceduralLane = z.infer<typeof ProceduralLaneSchema>;

export interface ProceduralActInfo {
  /** Libellé humain (rail + badges). */
  label: string;
  /** Articles CPP à afficher sous l'acte quand une source experte les a validés. */
  articles: string[];
  /**
   * Phases légalement compatibles avec l'acte. C'est la matrice de vérité pour
   * les validations DB/TS sur le couple (acte, phase).
   */
  allowed_phases: readonly ProceduralActPhase[];
  /**
   * Fallback de compatibilité pour les dossiers legacy. Null quand l'acte est
   * ambigu et qu'il faut attendre une phase explicitement sourcée.
   */
  legacy_fallback_phase: ProceduralActPhase | null;
  /**
   * Alias de transition conservé pour les anciens call sites. Il signifie le
   * fallback de compatibilité, pas la phase persistée en base.
   */
  phase: ProceduralActPhase | null;
  /** Voie logique (détermine la colonne via le mapping matière). */
  lane: ProceduralLane;
}

type ProceduralActInfoInput = Omit<ProceduralActInfo, 'phase'>;

function defineActInfo(info: ProceduralActInfoInput): ProceduralActInfo {
  return {
    ...info,
    phase: info.legacy_fallback_phase,
  };
}

export const PROCEDURAL_ACT_INFO: Record<ProceduralActType, ProceduralActInfo> = {
  // — enquête —
  plainte: defineActInfo({
    label: 'Plainte',
    articles: [],
    allowed_phases: ['enquete'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  pv_constatation: defineActInfo({
    label: 'PV de constatation',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  pv_audition_victime: defineActInfo({
    label: "PV d'audition victime",
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  pv_audition_temoin: defineActInfo({
    label: "PV d'audition témoin",
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  audition_libre: defineActInfo({
    label: 'Audition libre',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  garde_a_vue: defineActInfo({
    label: 'Garde à vue',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  perquisition: defineActInfo({
    label: 'Perquisition',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  saisie_scelle: defineActInfo({
    label: 'Saisie / scellé',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  requisition: defineActInfo({
    label: 'Réquisition',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  exploitation_video_telephonie: defineActInfo({
    label: 'Exploitation vidéo / téléphonie',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  prolongation_flagrance: defineActInfo({
    label: 'Prolongation de flagrance',
    articles: [],
    allowed_phases: ['enquete'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  passage_preliminaire: defineActInfo({
    label: 'Passage en préliminaire',
    articles: [],
    allowed_phases: ['enquete'],
    legacy_fallback_phase: 'enquete',
    lane: 'ossature',
  }),
  // — instruction —
  requisitoire_introductif: defineActInfo({
    label: 'Réquisitoire introductif',
    articles: ['art. 80 CPP'],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'ossature',
  }),
  commission_rogatoire: defineActInfo({
    label: 'Commission rogatoire',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'charge',
  }),
  interrogatoire_premiere_comparution: defineActInfo({
    label: 'Interrogatoire de première comparution',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'charge',
  }),
  mise_en_examen: defineActInfo({
    label: 'Mise en examen',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'charge',
  }),
  temoin_assiste: defineActInfo({
    label: 'Témoin assisté',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'charge',
  }),
  demande_acte: defineActInfo({
    label: "Demande d'acte / d'expertise",
    articles: ['art. 82-1 CPP', 'art. 156 CPP'],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'defense',
  }),
  ordonnance: defineActInfo({
    label: 'Ordonnance',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'ossature',
  }),
  expertise: defineActInfo({
    label: 'Expertise',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'charge',
  }),
  requisitoire_definitif: defineActInfo({
    label: 'Réquisitoire définitif',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'ossature',
  }),
  ordonnance_reglement: defineActInfo({
    label: 'Ordonnance de règlement',
    articles: ['art. 176-181 CPP'],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'ossature',
  }),
  // — jugement —
  citation_convocation: defineActInfo({
    label: 'Citation / convocation',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  audience: defineActInfo({
    label: 'Audience',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  jugement_correctionnel: defineActInfo({
    label: 'Jugement correctionnel',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  arret: defineActInfo({
    label: 'Arrêt',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  decision_cour_assises: defineActInfo({
    label: "Décision de cour d'assises",
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  appel: defineActInfo({
    label: 'Appel',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  pourvoi: defineActInfo({
    label: 'Pourvoi',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  // — legacy M110 —
  acte_enquete: defineActInfo({
    label: "Acte d'enquête",
    articles: [],
    allowed_phases: ['enquete'],
    legacy_fallback_phase: 'enquete',
    lane: 'charge',
  }),
  ordonnance_soit_communique: defineActInfo({
    label: 'Ordonnance de soit-communiqué',
    articles: [],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'ossature',
  }),
  jugement_fond: defineActInfo({
    label: 'Jugement au fond',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  requete_nullite: defineActInfo({
    label: 'Requête en nullité',
    articles: ['art. 170-174 CPP'],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'defense',
  }),
  demande_mise_en_liberte: defineActInfo({
    label: 'Demande de mise en liberté',
    articles: [],
    allowed_phases: ['instruction', 'jugement'],
    legacy_fallback_phase: 'instruction',
    lane: 'defense',
  }),
  observations_175: defineActInfo({
    label: 'Observations (art. 175)',
    articles: ['art. 175 CPP'],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'defense',
  }),
  appel_chambre_instruction: defineActInfo({
    label: "Appel devant la chambre de l'instruction",
    articles: ['art. 186 CPP'],
    allowed_phases: ['instruction'],
    legacy_fallback_phase: 'instruction',
    lane: 'defense',
  }),
  // — parquet / JLD / liberté —
  classement_sans_suite: defineActInfo({
    label: 'Classement sans suite',
    articles: [],
    allowed_phases: ['enquete'],
    legacy_fallback_phase: 'enquete',
    lane: 'ossature',
  }),
  alternative_poursuites: defineActInfo({
    label: 'Alternative aux poursuites',
    articles: [],
    allowed_phases: ['enquete'],
    legacy_fallback_phase: 'enquete',
    lane: 'ossature',
  }),
  saisine_juridiction_jugement: defineActInfo({
    label: 'Saisine juridiction de jugement',
    articles: [],
    allowed_phases: ['jugement'],
    legacy_fallback_phase: 'jugement',
    lane: 'ossature',
  }),
  saisine_jld: defineActInfo({
    label: 'Saisine du JLD',
    articles: [],
    allowed_phases: ['enquete', 'instruction'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
  debat_jld: defineActInfo({
    label: 'Débat JLD',
    articles: [],
    allowed_phases: ['enquete', 'instruction', 'jugement'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
  ordonnance_jld: defineActInfo({
    label: 'Ordonnance JLD',
    articles: [],
    allowed_phases: ['enquete', 'instruction', 'jugement'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
  placement_detention_provisoire: defineActInfo({
    label: 'Placement en détention provisoire',
    articles: [],
    allowed_phases: ['instruction', 'jugement'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
  placement_controle_judiciaire: defineActInfo({
    label: 'Placement sous contrôle judiciaire',
    articles: [],
    allowed_phases: ['instruction', 'jugement'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
  placement_arse: defineActInfo({
    label: 'Placement sous ARSE',
    articles: [],
    allowed_phases: ['instruction', 'jugement'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
  ordonnance_mise_en_liberte: defineActInfo({
    label: 'Ordonnance de mise en liberté',
    articles: [],
    allowed_phases: ['instruction', 'jugement'],
    legacy_fallback_phase: null,
    lane: 'ossature',
  }),
};

export interface ProceduralFactPhaseFields {
  procedural_act_type?: string | null;
  procedural_phase?: string | null;
  enquete_kind?: string | null;
}

export interface ProceduralFactPhaseValidationIssue {
  path: 'procedural_act_type' | 'procedural_phase' | 'enquete_kind';
  message: string;
}

export interface EffectiveProceduralPhaseInfo {
  phase: ProceduralActPhase | null;
  provenance: 'explicit' | 'legacy_fallback' | 'none';
  requires_phase_review: boolean;
}

export function isProceduralPhaseAllowedForActType(
  actType: string | null | undefined,
  phase: string | null | undefined,
): boolean {
  const parsedActType = ProceduralActTypeSchema.safeParse(actType);
  const parsedPhase = ProceduralActPhaseSchema.safeParse(phase);
  if (!parsedActType.success || !parsedPhase.success) {
    return false;
  }

  return PROCEDURAL_ACT_INFO[parsedActType.data].allowed_phases.includes(parsedPhase.data);
}

export function isUnambiguousProceduralActType(
  actType: string | null | undefined,
): boolean {
  const parsedActType = ProceduralActTypeSchema.safeParse(actType);
  if (!parsedActType.success) {
    return false;
  }

  return PROCEDURAL_ACT_INFO[parsedActType.data].allowed_phases.length === 1;
}

export function getDefaultEnqueteKind(
  actType: string | null | undefined,
): ProceduralEnqueteKind | null {
  const parsedActType = ProceduralActTypeSchema.safeParse(actType);
  if (!parsedActType.success) {
    return null;
  }

  switch (parsedActType.data) {
    case 'prolongation_flagrance':
      return 'flagrance';
    case 'passage_preliminaire':
      return 'preliminaire';
    default:
      return null;
  }
}

export function resolveEffectiveProceduralPhaseInfo(
  factLike: Pick<ProceduralFactPhaseFields, 'procedural_act_type' | 'procedural_phase'>,
): EffectiveProceduralPhaseInfo {
  const explicitPhase = ProceduralActPhaseSchema.safeParse(factLike.procedural_phase);
  if (explicitPhase.success) {
    return {
      phase: explicitPhase.data,
      provenance: 'explicit',
      requires_phase_review: false,
    };
  }

  const parsedActType = ProceduralActTypeSchema.safeParse(factLike.procedural_act_type);
  if (!parsedActType.success) {
    return {
      phase: null,
      provenance: 'none',
      requires_phase_review: false,
    };
  }

  const actInfo = PROCEDURAL_ACT_INFO[parsedActType.data];
  if (actInfo.legacy_fallback_phase !== null) {
    return {
      phase: actInfo.legacy_fallback_phase,
      provenance: 'legacy_fallback',
      requires_phase_review: actInfo.allowed_phases.length > 1,
    };
  }

  return {
    phase: null,
    provenance: 'none',
    requires_phase_review: actInfo.allowed_phases.length > 1,
  };
}

/**
 * Helper de compatibilité pour les call sites legacy qui ne consomment pas
 * encore la provenance du fallback.
 */
export function resolveEffectiveProceduralPhase(
  factLike: Pick<ProceduralFactPhaseFields, 'procedural_act_type' | 'procedural_phase'>,
): ProceduralActPhase | null {
  return resolveEffectiveProceduralPhaseInfo(factLike).phase;
}

/**
 * Phase pénale dérivée du catalogue partagé. Accepte `string` pour rester robuste
 * côté read-model legacy : une valeur hors enum revient à `null`, jamais à une
 * phase inventée dans le frontend.
 */
export function getProceduralActPhase(
  actType: string | null | undefined,
): ProceduralActPhase | null {
  const parsed = ProceduralActTypeSchema.safeParse(actType);
  if (!parsed.success) return null;
  return PROCEDURAL_ACT_INFO[parsed.data].legacy_fallback_phase;
}

export interface ProceduralActPhaseMismatch {
  actType: string;
  phase: string;
  /**
   * À renseigner seulement quand le couple refusé résulte de la fusion de
   * l'appel avec la ligne déjà en base (`update_fact` n'envoie souvent qu'un
   * des deux champs). Sans cette origine, l'agent ne voit dans son propre appel
   * qu'une valeur valide et ne peut pas deviner que l'autre vient de la base.
   */
  mergedWithStoredRow?: {
    actTypeFromStoredRow: boolean;
    phaseFromStoredRow: boolean;
  };
}

/**
 * Message de refus unique pour un couple `procedural_act_type` /
 * `procedural_phase` incompatible, partagé par la validation de schéma et par
 * le repository.
 *
 * Deux chemins refusaient le même défaut avec deux textes : le schéma listait
 * les phases admises, le repository renvoyait au « catalogue partagé » sans les
 * donner ni dire qu'une des deux valeurs venait de la base (≈420 refus par
 * semaine, audit #2041). Un seul constructeur empêche les deux textes de
 * diverger à nouveau.
 */
export function buildProceduralActPhaseMismatchMessage(
  mismatch: ProceduralActPhaseMismatch,
): string {
  const { actType, phase, mergedWithStoredRow } = mismatch;
  const parsedActType = ProceduralActTypeSchema.safeParse(actType);
  const allowedPhases = parsedActType.success
    ? PROCEDURAL_ACT_INFO[parsedActType.data].allowed_phases
    : null;
  const allowed =
    allowedPhases === null
      ? `inconnues, procedural_act_type "${actType}" est hors catalogue`
      : allowedPhases.length > 0
        ? allowedPhases.join(', ')
        : 'aucune phase : omets-la';
  const refusal =
    `procedural_act_type ${actType} cannot be stored with procedural_phase ${phase}; ` +
    `allowed phases: ${allowed}`;
  if (mergedWithStoredRow === undefined) return refusal;

  const origin = (fromStoredRow: boolean): string =>
    fromStoredRow
      ? 'vient de la ligne déjà en base (ton appel ne l\'a pas fourni)'
      : 'vient de ton appel';
  return (
    `${refusal}. ` +
    `Origine : procedural_act_type "${actType}" ${origin(mergedWithStoredRow.actTypeFromStoredRow)} ; ` +
    `procedural_phase "${phase}" ${origin(mergedWithStoredRow.phaseFromStoredRow)}. ` +
    'Geste : envoie procedural_act_type et procedural_phase ensemble, ' +
    'ou clear_procedural_phase: true pour retirer la phase.'
  );
}

export interface ProceduralFactValidationOptions {
  /**
   * `update_fact` peut recevoir uniquement `enquete_kind` et laisser la couche
   * repository arbitrer le couple final avec la ligne existante.
   */
  allow_implicit_existing_enquete_phase_for_kind?: boolean;
}

export function getProceduralFactPhaseValidationIssues(
  factLike: ProceduralFactPhaseFields,
  options: ProceduralFactValidationOptions = {},
): ProceduralFactPhaseValidationIssue[] {
  const issues: ProceduralFactPhaseValidationIssue[] = [];
  const parsedActType = ProceduralActTypeSchema.safeParse(factLike.procedural_act_type);
  const parsedPhase = ProceduralActPhaseSchema.safeParse(factLike.procedural_phase);
  const parsedEnqueteKind = ProceduralEnqueteKindSchema.safeParse(factLike.enquete_kind);

  const explicitPhase = parsedPhase.success ? parsedPhase.data : null;
  const enqueteKind =
    factLike.enquete_kind == null
      ? null
      : parsedEnqueteKind.success
        ? parsedEnqueteKind.data
        : null;

  if (
    enqueteKind !== null &&
    explicitPhase !== 'enquete' &&
    !(
      options.allow_implicit_existing_enquete_phase_for_kind === true &&
      factLike.procedural_phase === undefined
    )
  ) {
    issues.push({
      path: 'enquete_kind',
      message:
        "enquete_kind requires an explicit procedural_phase='enquete' on the same fact row; store NULL when the source does not establish the enquiry phase",
    });
  }

  if (parsedActType.success && explicitPhase !== null) {
    const actInfo = PROCEDURAL_ACT_INFO[parsedActType.data];
    if (!actInfo.allowed_phases.includes(explicitPhase)) {
      issues.push({
        path: 'procedural_phase',
        message: buildProceduralActPhaseMismatchMessage({
          actType: parsedActType.data,
          phase: explicitPhase,
        }),
      });
    }
  }

  return issues;
}

/** Position visuelle dans le rail Procédure. */
export type RailColumn = 'left' | 'center' | 'right';

/**
 * Orientation des colonnes en matière pénale (instruction), telle que demandée
 * par l'expert métier (Marcus, 2026-06-05) : actes à charge à GAUCHE, ossature
 * parquet→juge au CENTRE, recours de la défense à DROITE.
 */
export const PENAL_LANE_COLUMN: Record<ProceduralLane, RailColumn> = {
  charge: 'left',
  ossature: 'center',
  defense: 'right',
};

/** Colonne du rail pénal pour un `procedural_act_type` donné. */
export function penalColumnForActType(actType: ProceduralActType): RailColumn {
  return PENAL_LANE_COLUMN[PROCEDURAL_ACT_INFO[actType].lane];
}
